import { connect, ResolveThunks } from "react-redux";
import TagArray from "components/TagArray";
import FeedbackSelect from "pages/ManageLikesPage/components/FeedbackSelect";
import { rootSelectors, RootState } from "state/rootReducer";
import {
  BlockIcon,
  DislikeIcon,
  ICON_STROKE,
  LikeIcon,
  PlayIcon,
  SeedIcon,
} from "shared/icons";
import {
  SongFeedbackAndSeed,
  actions as userActions,
} from "state/slices/userSlice";

interface OwnProps {
  addBorder: boolean;
  trackId: string;
  hasFeedback: boolean;
  currentFeedback: SongFeedbackAndSeed | undefined;
  onAddFeedback: (trackId: string, type: FeedbackType) => void;
  onPlaySong?: () => void;
  /** Names the song in the feedback dropdown's accessible label. */
  songName?: string;
  /**
   * On phones, once the song has feedback, show it as the Feedback table's
   * dropdown instead of four buttons. The tags appear at that point too, and
   * this leaves room for them on the same line.
   */
  compactOnPhones?: boolean;
}

interface StateProps {
  enabledTags: Tag[];
}

interface DispatchProps {
  onAddTag: typeof userActions.addTag;
  onRemoveTag: typeof userActions.removeTag;
}

type SongActionBarProps = OwnProps & StateProps & ResolveThunks<DispatchProps>;

/**
 * The four judgements a track can receive, in the order they appear. Each one
 * lights up in its own colour when active, so the row's state reads without
 * having to decode a glyph: seed is the brand accent, like is Spotify green,
 * the two rejections are neutral and red.
 */
const FEEDBACK_ACTIONS: {
  feedback: FeedbackType;
  label: string;
  Icon: typeof LikeIcon;
  activeClass: string;
}[] = [
  {
    feedback: "Seed",
    label: "Use as seed",
    Icon: SeedIcon,
    activeClass: "text-accent bg-accent-dim",
  },
  {
    feedback: "Like",
    label: "Like",
    Icon: LikeIcon,
    activeClass: "text-spotify bg-raised",
  },
  {
    feedback: "Do Not Play",
    label: "Do not play",
    Icon: BlockIcon,
    activeClass: "text-danger bg-raised",
  },
  {
    feedback: "Dislike",
    label: "Dislike",
    Icon: DislikeIcon,
    activeClass: "text-ink bg-raised",
  },
];

const BUTTON_BASE =
  "flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded " +
  "transition-colors cursor-pointer";

export function SongActionBar(props: SongActionBarProps): JSX.Element {
  const borderClass = props.addBorder ? "bordered" : ``;
  const current = props.currentFeedback;
  const collapseOnPhones = !!props.compactOnPhones && current !== undefined;

  return (
    <div
      className={`flex flex-row flex-wrap items-center gap-x-1 gap-y-2 ${borderClass}`}
    >
      {props.onPlaySong && (
        <button
          type="button"
          className={`${BUTTON_BASE} text-ink-2 hover:text-ink hover:bg-row-hover`}
          onClick={() => props.onPlaySong!()}
          aria-label="Play"
          title="Play"
        >
          <PlayIcon size={17} strokeWidth={ICON_STROKE} aria-hidden="true" />
        </button>
      )}

      {collapseOnPhones && (
        <div className="sm:hidden">
          <FeedbackSelect
            value={current.feedback}
            songName={props.songName ?? "this song"}
            onChange={(value) => props.onAddFeedback(props.trackId, value)}
          />
        </div>
      )}

      {/* `contents` makes the wrapper disappear from layout, so the buttons stay
          items of this row. When collapsed they're hidden only below sm. */}
      <div className={collapseOnPhones ? "hidden sm:contents" : "contents"}>
        {FEEDBACK_ACTIONS.map(({ feedback, label, Icon, activeClass }) => {
          const isActive = current?.feedback === feedback;

          return (
            <button
              key={feedback}
              type="button"
              aria-pressed={isActive}
              aria-label={label}
              title={label}
              className={`${BUTTON_BASE} ${
                isActive
                  ? activeClass
                  : "text-muted hover:text-ink hover:bg-row-hover"
              }`}
              onClick={() => props.onAddFeedback(props.trackId, feedback)}
            >
              <Icon size={17} strokeWidth={ICON_STROKE} aria-hidden="true" />
            </button>
          );
        })}
      </div>

      {/* The tags move as one unit: they sit beside the buttons when they fit
          and wrap beneath them when they don't, rather than running off the
          screen. They used to be forced onto their own line on phones, which
          in the listen bar also pushed "Open Spotify" to a third line. The
          divider only shows from sm; below that a small margin separates
          the tags from the buttons instead. */}
      {props.hasFeedback && (
        <div className="flex items-center ml-1 sm:ml-0">
          <div
            className="hidden sm:block mx-2 h-5 w-px bg-rule-strong"
            aria-hidden="true"
          ></div>
          <TagArray
            enabledTags={props.enabledTags}
            onSelect={(tag) => props.onAddTag(props.trackId, tag)}
            onDeselect={(tag) => props.onRemoveTag(props.trackId, tag)}
          />
        </div>
      )}
    </div>
  );
}

function mapState(state: RootState, ownProps: OwnProps): StateProps {
  return {
    enabledTags: rootSelectors.user.getEnabledTags(state, ownProps.trackId),
  };
}

const mapDispatch: DispatchProps = {
  onAddTag: userActions.addTag,
  onRemoveTag: userActions.removeTag,
};

export default connect(mapState, mapDispatch)(SongActionBar);
